import {useCallback, useMemo} from 'react';
import {Blocker} from 'react-router';
import {ConfirmDialogActions} from '@/components/confirm-dialog/confirm-dialog';
import {BUTTON_THEME} from '@/constants/button';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';
import {useDocumentLocale} from './use-document-locale';

type Props = {
  blocker: Blocker;
  saveChanges: () => Promise<void>;
};

export const useNavigationConfirmationActions = ({blocker, saveChanges}: Props): ConfirmDialogActions[] => {
  const {t} = useDocumentLocale();

  const onCancelNavigation = useCallback(() => {
    blocker.reset?.();
  }, [blocker]);

  const onConfirmDiscard = useCallback(() => {
    blocker.proceed?.();
  }, [blocker]);

  const onConfirmSave = useCallback(async () => {
    await saveChanges();

    blocker.proceed?.();
  }, [blocker, saveChanges]);

  return useMemo(() => {
    return [
      {
        type: 'cancel',
        testId: 'confirm-dialog-cancel-button',
        trackingKey: WEB_CLICK_TRACKING_KEY.CANCEL_CONFIRMATION,
        theme: BUTTON_THEME.TERTIARY,
        label: t('components.document.navigation-confirm.cancel'),
        onAction: onCancelNavigation,
      },
      {
        type: 'confirm-discard',
        testId: 'confirm-dialog-discard-button',
        trackingKey: WEB_CLICK_TRACKING_KEY.CONFIRM_DISCARD,
        theme: BUTTON_THEME.DANGER,
        label: t('components.document.navigation-confirm.confirm-discard'),
        onAction: onConfirmDiscard,
      },
      {
        type: 'confirm-save',
        testId: 'confirm-dialog-save-button',
        trackingKey: WEB_CLICK_TRACKING_KEY.CONFIRM_SAVE,
        label: t('components.document.navigation-confirm.confirm-save'),
        onAction: onConfirmSave,
      },
    ];
  }, [onCancelNavigation, onConfirmDiscard, onConfirmSave, t]);
};
